"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  role: string;
  is_active: boolean;
  created_at: string;
  assigned_destination_ids: string[];
  permissions?: string[];
  avatar_url?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  impersonating?: boolean;
  impersonator_id?: string | null;
  workspace_rewrite_prompt?: string | null;
  workspace_rewrite_model?: string | null;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  isImpersonating: boolean;
  hasPermission: (key: string) => boolean;
  applyUser: (user: AuthUser) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  token: null,
  loading: true,
  isImpersonating: false,
  hasPermission: () => false,
  applyUser: () => {},
  login: async () => {},
  logout: () => {},
  impersonate: async () => {},
  stopImpersonation: async () => {},
});

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const TOKEN_KEY = "alephia_token";
const USER_KEY = "alephia_user";
const ORIGINAL_TOKEN_KEY = "alephia_original_token";
const ORIGINAL_USER_KEY = "alephia_original_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));

      fetch(`${API_URL}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      })
        .then(async (res) => {
          if (res.ok) {
            const u = await res.json();
            setUser(u);
            localStorage.setItem(USER_KEY, JSON.stringify(u));
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setToken(null);
            setUser(null);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: "Eroare de autentificare" }));
      throw new Error(body.detail || "Eroare de autentificare");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  const impersonate = useCallback(async (userId: string) => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    const currentUserStr = localStorage.getItem(USER_KEY);
    if (!currentToken) throw new Error("Nu ești autentificat");

    const res = await fetch(`${API_URL}/api/v1/auth/impersonate/${userId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: "Eroare la impersonare" }));
      throw new Error(body.detail || "Eroare la impersonare");
    }
    const data = await res.json();

    if (!localStorage.getItem(ORIGINAL_TOKEN_KEY)) {
      localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
      if (currentUserStr) localStorage.setItem(ORIGINAL_USER_KEY, currentUserStr);
    }

    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const stopImpersonation = useCallback(async () => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (!currentToken) throw new Error("Nu ești autentificat");

    const res = await fetch(`${API_URL}/api/v1/auth/stop-impersonate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: "Eroare la revenire" }));
      throw new Error(body.detail || "Eroare la revenire");
    }
    const data = await res.json();

    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_USER_KEY);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const isImpersonating = !!user?.impersonating;

  const hasPermission = useCallback(
    (key: string) => !!user?.permissions?.includes(key),
    [user],
  );

  const applyUser = useCallback((u: AuthUser) => {
    setUser(u);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, isImpersonating, hasPermission, applyUser, login, logout, impersonate, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}
